import React, { useState } from 'react';
import {
  ButtonSetWrapper,
  ConfirmPasswordField,
  NewPasswordField,
  ResetPasswordFormInputGrid,
} from '../components/shared/StyledComponents';
import {
  FORM_IDS,
  formDefaultValues,
  useLoginContext,
} from 'providers/LoginProvider';
import { Button, useFormInputHandler } from 'swipeclock-frontend';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormValidation } from '../loginValidation';
import { useLocation, useHistory } from 'react-router-dom';
import { usePostUpdatePasswordMutation } from 'services/login';
import WhiteLabelLogo from '../components/shared/WhiteLabelLogo';
import PoweredByLabel from '../components/shared/PoweredByLabel';
import AnimatedAlert from '../components/shared/AnimatedAlert';
import { useWhiteLabelContext } from 'providers/WhiteLabelProvider';

export default function ResetPasswordClock() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const location = useLocation();
  const [updatePassword, { isLoading }] = usePostUpdatePasswordMutation();
  const [resetSuccess, setResetSuccess] = useState(false);

  const { errorContext, validationCallbackReset } = useFormValidation();

  const {
    actions,
    meta: { pages },
  } = useLoginContext();
  const { state: whiteLabelState } = useWhiteLabelContext();

  const {
    disabled,
    getError,
    handleBlur,
    handleChange,
    isValid,
    setDirty,
    validationRef,
  } = useFormInputHandler({
    errorContext,
    ids: [FORM_IDS.NEWPASSWORD, FORM_IDS.CONFIRMPASSWORD],
    validationCallback: validationCallbackReset,
  });

  const handleResetPassword = async () => {
    try {
      if (!isValid(pages.RESETPASS)) {
        throw new Error('Form is invalid.');
      }
      const priorState = location.state || {};
      const { site, password, username, jwt } = priorState;
      const body = {
        Id: username,
        IdType: 'employeeCode',
        CurrentPassword: password,
        NewPassword: validationRef.current[FORM_IDS.NEWPASSWORD]?.value,
        jwt,
        site,
      };
      const response = await updatePassword({ body }).unwrap();
      console.log('update password successful: ', response);
      const message = 'Success! Your password has been changed.';
      actions.setGlobalMessage({ message, severity: 'success' });
      setResetSuccess(true);
    } catch (error) {
      console.error(error);
      const errorText = `We're sorry, we were unable to reset your password. Please try again, or contact an administrator for assistance.`;
      actions.setGlobalMessage({ message: errorText, severity: 'error' });
    }
  };

  const handleBackToLogin = () => {
    actions.setCurrentPage(pages.START);
    actions.setGlobalMessage();
    history.replace('/clock/web/login');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleResetPassword();
      setDirty((prevDirty) => ({
        ...prevDirty,
        [FORM_IDS.CONFIRMPASSWORD]: true,
      }));
    }
  };

  return (
    <ResetPasswordFormInputGrid
      noHeader={whiteLabelState?.noHeader}
      isMobile={isMobile}
    >
      <WhiteLabelLogo
        whiteLabelState={whiteLabelState}
        headerText="Reset Password"
      />
      <AnimatedAlert />
      <NewPasswordField
        autoComplete="newPassword"
        error={getError(FORM_IDS.NEWPASSWORD)}
        id={FORM_IDS.NEWPASSWORD}
        label="New Password"
        maxLength={50}
        onBlur={handleBlur(FORM_IDS.NEWPASSWORD)}
        onChange={handleChange(FORM_IDS.NEWPASSWORD)}
        defaultValue={formDefaultValues.newPassword}
        placeholder="Enter a New Password"
        required
        validationRef={validationRef}
        size="small"
      />
      <ConfirmPasswordField
        autoComplete="confirmPassword"
        error={getError(FORM_IDS.CONFIRMPASSWORD)}
        id={FORM_IDS.CONFIRMPASSWORD}
        label="Confirm Password"
        maxLength={50}
        onBlur={handleBlur(FORM_IDS.CONFIRMPASSWORD)}
        onChange={handleChange(FORM_IDS.CONFIRMPASSWORD)}
        defaultValue={formDefaultValues.confirmPassword}
        placeholder="Re-enter Your New Password"
        required
        validationRef={validationRef}
        inputProps={{ onKeyDown: handleKeyDown }}
        size="small"
      />
      <ButtonSetWrapper isMobile={isMobile}>
        {resetSuccess && (
          <Button
            id="sc-mandatory_reset_back_to_login_button"
            size="large"
            onClick={handleBackToLogin}
          >
            Back to Login
          </Button>
        )}
        {!resetSuccess && (
          <>
            <Button
              id="sc-cancel_button"
              size="large"
              variant="outlined"
              onClick={handleBackToLogin}
            >
              Cancel
            </Button>
            <Button
              id="sc-mandatory_reset_button"
              size="large"
              onClick={handleResetPassword}
              disabled={disabled || !isValid(pages.RESETPASS)}
              loading={isLoading}
            >
              Reset
            </Button>
          </>
        )}
      </ButtonSetWrapper>
      <PoweredByLabel whiteLabelState={whiteLabelState} />
    </ResetPasswordFormInputGrid>
  );
}
